import { createWebCore } from "./core";
import type { WebCoreOptions } from "./types";

type IWQueueEntry = [string, unknown[]];

type IWInstance = {
	_q?: IWQueueEntry[];
	init?: (...args: unknown[]) => unknown;
	start?: (...args: unknown[]) => unknown;
	stop?: (...args: unknown[]) => unknown;
	hardStop?: (...args: unknown[]) => unknown;
	flush?: (...args: unknown[]) => unknown;
	identify?: (...args: unknown[]) => unknown;
	tagSession?: (...args: unknown[]) => unknown;
	trackCustomEvent?: (...args: unknown[]) => unknown;
	generateInsights?: (...args: unknown[]) => unknown;
	getSessionId?: (...args: unknown[]) => unknown;
	getSessionInfo?: (...args: unknown[]) => unknown;
	isBlocked?: (...args: unknown[]) => unknown;
	getContractStatus?: (...args: unknown[]) => unknown;
	getQuotaStatus?: (...args: unknown[]) => unknown;
	[key: string]: unknown;
};

type IWWatchWindow = Window & {
	__IW_LOADED?: boolean;
	__IW_SNIPPET_LOADED?: boolean;
	__IW_CONFIG__?: Record<string, unknown>;
	InfiniteWatch?: IWInstance;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null;

(() => {
	if (typeof window === "undefined" || typeof document === "undefined") return;

	const iwWindow = window as IWWatchWindow;

	if (iwWindow.__IW_LOADED || iwWindow.__IW_SNIPPET_LOADED) return;
	iwWindow.__IW_LOADED = true;
	iwWindow.__IW_SNIPPET_LOADED = true;

	const currentScript =
		(document.currentScript as HTMLScriptElement | null) ||
		document.querySelector("script[data-iw-organization-id]");

	const configFromGlobal = isRecord(iwWindow.__IW_CONFIG__)
		? iwWindow.__IW_CONFIG__
		: {};
	const options: Record<string, unknown> = { ...configFromGlobal };

	const existing = iwWindow.InfiniteWatch;
	const queue: IWQueueEntry[] = Array.isArray(existing?._q) ? existing._q : [];

	const readAttr = (name: string) => currentScript?.getAttribute(name);
	const organizationId =
		readAttr("data-iw-organization-id") ?? configFromGlobal.organizationId;
	const hasQueuedInit = queue.some(([method]) => method === "init");

	if (!organizationId && !hasQueuedInit) return;
	if (organizationId) {
		options.organizationId = organizationId;
	}

	const endpoint = readAttr("data-iw-endpoint") ?? configFromGlobal.endpoint;
	if (endpoint !== undefined) {
		options.endpoint = endpoint;
	}

	const baseUrl = readAttr("data-iw-base-url") ?? configFromGlobal.baseUrl;
	if (baseUrl !== undefined) {
		options.baseUrl = baseUrl;
	}

	const endpointConfig =
		readAttr("data-iw-endpoint-config") ?? configFromGlobal.endpointConfig;
	if (endpointConfig !== undefined) {
		options.endpointConfig = endpointConfig;
	}

	const sessionId =
		readAttr("data-iw-session-id") ?? configFromGlobal.sessionId;
	if (sessionId !== undefined) {
		options.sessionId = sessionId;
	}

	const userId = readAttr("data-iw-user-id") ?? configFromGlobal.userId;
	if (userId !== undefined) {
		options.userId = userId;
	}

	const debugAttr = readAttr("data-iw-debug") ?? configFromGlobal.debug;
	if (debugAttr !== undefined) {
		options.debug = debugAttr === true || debugAttr === "true" || debugAttr === "";
	}

	const minSessionDurationAttr =
		readAttr("data-iw-min-session-duration") ?? configFromGlobal.minSessionDuration;
	if (minSessionDurationAttr !== undefined) {
		const parsed = Number(minSessionDurationAttr);
		if (!Number.isNaN(parsed)) {
			options.minSessionDuration = parsed;
		}
	}

	const enqueue = (method: string, args: unknown[]) => {
		queue.push([method, args]);
	};

	const makeProxy = (method: string) => {
		return (...args: unknown[]): IWInstance => {
			enqueue(method, args);
			return iwWindow.InfiniteWatch as IWInstance;
		};
	};

	const shim: IWInstance = {
		_q: queue,
		init: makeProxy("init"),
		start: makeProxy("start"),
		stop: makeProxy("stop"),
		hardStop: makeProxy("hardStop"),
		flush: makeProxy("flush"),
		identify: makeProxy("identify"),
		tagSession: makeProxy("tagSession"),
		trackCustomEvent: makeProxy("trackCustomEvent"),
		generateInsights: makeProxy("generateInsights"),
		getSessionId: makeProxy("getSessionId"),
	};

	iwWindow.InfiniteWatch = { ...(existing || {}), ...shim };

	if (!hasQueuedInit) {
		enqueue("init", [options as WebCoreOptions]);
	}

	if (!queue.some(([method]) => method === "start")) {
		enqueue("start", []);
	}

	const core = createWebCore("watch-js");
	const drainQueue = async () => {
		for (const [method, args] of queue) {
			const fn = (core as Record<string, unknown>)[method];
			if (typeof fn === "function") {
				await (fn as (...callArgs: unknown[]) => unknown)(...args);
			}
		}
		queue.length = 0;
	};

	iwWindow.InfiniteWatch = core as IWInstance;
	void drainQueue();
})();
